import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import {
  grantMarketingConsent,
  revokeMarketingConsent,
  getStoredConsent,
} from '../utils/googleAds';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Technisch notwendig, kann nicht deaktiviert werden
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    // Banner nur anzeigen, wenn KEIN gültiger (nicht abgelaufener) Consent existiert
    const existing = getStoredConsent();
    if (!existing) {
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (type) => {
    const finalPrefs =
      type === 'all'
        ? { necessary: true, functional: true, marketing: true }
        : type === 'necessary'
        ? { necessary: true, functional: false, marketing: false }
        : preferences;

    const consentData = {
      type: type,
      preferences: finalPrefs,
      timestamp: new Date().toISOString(),
      version: '2.1',
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));

    // Google Consent Mode v2 entsprechend aktualisieren
    if (finalPrefs.marketing) {
      grantMarketingConsent();
    } else {
      revokeMarketingConsent();
    }

    setIsVisible(false);
  };

  const acceptAll = () => saveConsent('all');

  const acceptSelected = () => saveConsent('selected');

  const acceptNecessary = () => {
    setPreferences({ necessary: true, functional: false, marketing: false });
    saveConsent('necessary');
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          data-testid="cookie-banner"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-desc"
          className="fixed bottom-0 left-0 right-0 z-[60] p-4"
        >
          <div className="max-w-4xl mx-auto bg-tiktok-elevated rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-tiktok-cyan/20 to-tiktok-pink/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-tiktok-cyan" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <h2
                    id="cookie-banner-title"
                    className="font-bold text-white text-lg mb-2"
                  >
                    Datenschutz-Einstellungen
                  </h2>
                  <p
                    id="cookie-banner-desc"
                    className="text-sm text-gray-400 leading-relaxed"
                  >
                    Wir nutzen standardmäßig nur technisch notwendige Funktionen für den Betrieb dieser Website.
                    Optional setzen wir <strong className="text-white">Google Ads Conversion-Tracking</strong> ein, um den Erfolg unserer Anzeigen zu messen.
                    Dabei können anonymisierte Daten an <strong className="text-white">Google Ireland Ltd. / Google LLC (USA)</strong> übertragen werden.
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Ohne Ihre Zustimmung werden <strong className="text-white">keine</strong> Marketing-Daten übertragen.
                    Sie können Ihre Einwilligung jederzeit über den Link „Cookie-Einstellungen" im Footer widerrufen – die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt davon unberührt.
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Details in unserer{' '}
                    <Link to="/datenschutz" className="text-tiktok-cyan hover:underline">
                      Datenschutzerklärung
                    </Link>
                    .
                  </p>
                </div>
                <button
                  onClick={acceptNecessary}
                  data-testid="cookie-close-btn"
                  className="text-gray-500 hover:text-white transition-colors p-1"
                  aria-label="Schließen – nur notwendige akzeptieren"
                  title="Schließen – nur notwendige akzeptieren"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Details Toggle */}
            <div className="px-6">
              <button
                onClick={() => setShowDetails(!showDetails)}
                data-testid="cookie-details-toggle"
                className="flex items-center gap-2 text-sm text-tiktok-cyan hover:text-white transition-colors"
                aria-expanded={showDetails}
              >
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showDetails ? 'Details ausblenden' : 'Einstellungen anpassen'}
              </button>
            </div>

            {/* Details Panel */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 py-4 space-y-4">
                    {/* Necessary */}
                    <div className="p-4 bg-tiktok-black/50 rounded-xl border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-green-500" aria-hidden="true" />
                          </div>
                          <span className="font-semibold text-white">Technisch notwendig</span>
                        </div>
                        <span className="text-xs text-green-500 bg-green-500/10 px-2 py-1 rounded">
                          Immer aktiv
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 ml-11">
                        Für den Betrieb der Website erforderlich. Speichert lediglich Ihre Datenschutz-Einstellung (Local Storage). Keine Übertragung an Dritte. Rechtsgrundlage: § 25 Abs. 2 Nr. 2 TDDDG.
                      </p>
                    </div>

                    {/* Functional */}
                    <div className="p-4 bg-tiktok-black/50 rounded-xl border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-tiktok-cyan/20 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-tiktok-cyan" aria-hidden="true" />
                          </div>
                          <span className="font-semibold text-white">Funktional</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="cookie-toggle-functional"
                            checked={preferences.functional}
                            onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                            className="sr-only peer"
                            aria-label="Funktionale Cookies erlauben"
                          />
                          <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-tiktok-cyan"></div>
                        </label>
                      </div>
                      <p className="text-sm text-gray-400 ml-11">
                        Ermöglicht erweiterte Komfortfunktionen wie das Speichern von Formular-Eingaben für spätere Besuche. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO.
                      </p>
                    </div>

                    {/* Marketing / Ads */}
                    <div className="p-4 bg-tiktok-black/50 rounded-xl border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-tiktok-pink/20 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-tiktok-pink" aria-hidden="true" />
                          </div>
                          <span className="font-semibold text-white">Marketing (Google Ads)</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="cookie-toggle-marketing"
                            checked={preferences.marketing}
                            onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                            className="sr-only peer"
                            aria-label="Marketing-Tracking erlauben"
                          />
                          <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-tiktok-pink"></div>
                        </label>
                      </div>
                      <p className="text-sm text-gray-400 ml-11">
                        Lädt Google Tag Manager (gtag.js) und erlaubt das Conversion-Tracking unserer Werbeanzeigen. Anbieter: Google Ireland Ltd. (EU), ggf. Datenübertragung an Google LLC, USA (EU-US Data Privacy Framework). Speicherdauer Cookies: bis zu 24 Monate. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO i.V.m. § 25 Abs. 1 TDDDG.
                      </p>
                    </div>

                    {/* Info Box */}
                    <div className="p-4 bg-tiktok-cyan/5 rounded-xl border border-tiktok-cyan/20">
                      <p className="text-sm text-gray-300">
                        <strong className="text-white">Was wir NICHT nutzen:</strong> Wir setzen weder Google Analytics, Facebook Pixel, Hotjar noch andere Profiling- oder Re-Targeting-Tools ein. Der WhatsApp-Button öffnet ausschließlich einen Link – es werden keine Daten an WhatsApp übertragen, bis Sie aktiv eine Nachricht senden.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions – gleichberechtigte Buttons (BGH I ZR 186/23, DSK 2024) */}
            <div className="p-6 pt-4 flex flex-col sm:flex-row gap-3 border-t border-white/5">
              <Button
                onClick={acceptNecessary}
                data-testid="cookie-accept-necessary"
                className="flex-1 bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/20"
              >
                Ablehnen
              </Button>
              {showDetails && (
                <Button
                  onClick={acceptSelected}
                  data-testid="cookie-accept-selected"
                  className="flex-1 bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/20"
                >
                  Auswahl speichern
                </Button>
              )}
              <Button
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="flex-1 bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/20"
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
